import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Sparkles, Play, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, pollJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ISTATUS = { in_progress: ["In progress", "bg-sky-500/15 text-sky-700 dark:text-sky-300"], completed: ["Passed", "bg-health/15 text-health"], actions_open: ["Actions open", "bg-[#FFA000]/15 text-[#B87300]"], closed: ["Closed — actions verified", "bg-slate-500/15 text-slate-600 dark:text-slate-300"] };
const MANAGERS = ["owner", "admin", "hse_manager", "supervisor", "project_manager"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

const StartDialog = ({ t, close }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ location: "", project_ref: "" });
  const go = async () => {
    try {
      const { data } = await api.post("/inspections", { template_id: t.id, ...f });
      nav(`/app/inspections/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={close}><DialogContent><DialogHeader><DialogTitle>Start: {t.name}</DialogTitle></DialogHeader>
      <div className="space-y-3"><div className="space-y-1"><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Site, building or area" data-testid="start-location" /></div>
        <div className="space-y-1"><Label>Project / job reference (optional)</Label><Input value={f.project_ref} onChange={(e) => setF({ ...f, project_ref: e.target.value })} /></div>
        <Button className="w-full" onClick={go} data-testid="start-inspection-go"><Play className="mr-1.5 h-4 w-4" />Start inspection</Button></div></DialogContent></Dialog>
  );
};

const TemplateEditor = ({ t, close, saved }) => {
  const [f, setF] = useState({ ...t, items: t.items.map((x) => ({ ...x })) });
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  const save = async (status) => {
    try {
      await api.put(`/inspections/templates/${t.id}`, { name: f.name, category: f.category, frequency: f.frequency, description: f.description || "", items: f.items, status });
      toast.success(status === "published" ? "Checklist published" : status === "archived" ? "Checklist archived" : "Saved");
      saved();
      close();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={close}><DialogContent className="max-h-[90vh] max-w-3xl overflow-auto"><DialogHeader><DialogTitle>Edit checklist</DialogTitle></DialogHeader>
      {t.ai_generated && t.status === "draft" && <p className="rounded-xl bg-[#FFA000]/10 p-3 text-xs">Drafted by AI — check every item suits your workplace before publishing.</p>}
      <div className="grid gap-3 sm:grid-cols-3"><Input className="sm:col-span-2" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tpl-name" />
        <select className="rounded-md border bg-background px-3 py-2 text-sm" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}>{["daily", "weekly", "monthly", "ad_hoc"].map((x) => <option key={x} value={x}>{x.replace("_", "-")}</option>)}</select></div>
      <div className="space-y-2">{f.items.map((x, i) => (
        <div key={x.id || i} className="grid gap-2 rounded-xl border p-3 sm:grid-cols-12">
          <Input className="sm:col-span-3" value={x.section} onChange={(e) => setItem(i, "section", e.target.value)} placeholder="Section" />
          <Input className="sm:col-span-5" value={x.text} onChange={(e) => setItem(i, "text", e.target.value)} placeholder="Check" data-testid={`tpl-item-${i}`} />
          <select className="rounded-md border bg-background px-2 text-sm sm:col-span-2" value={x.type} onChange={(e) => setItem(i, "type", e.target.value)}><option value="pass_fail">Pass/Fail</option><option value="yes_no">Yes/No</option><option value="number">Number</option><option value="text">Text</option></select>
          <div className="flex items-center gap-2 text-xs sm:col-span-2"><label className="flex items-center gap-1"><input type="checkbox" checked={x.photo_on_fail} onChange={(e) => setItem(i, "photo_on_fail", e.target.checked)} />Photo on fail</label>
            <button onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} aria-label="Remove"><Trash2 className="h-4 w-4 text-alert" /></button></div>
        </div>))}</div>
      <Button variant="outline" size="sm" onClick={() => setF({ ...f, items: [...f.items, { section: f.items.at(-1)?.section || "General", text: "", type: "pass_fail", required: true, photo_on_fail: false }] })} data-testid="tpl-add-item"><Plus className="mr-1 h-3 w-3" />Add check</Button>
      <div className="flex flex-wrap gap-2 border-t pt-3"><Button onClick={() => save("published")} data-testid="tpl-publish">Save & publish</Button><Button variant="outline" onClick={() => save("draft")}>Save as draft</Button><Button variant="ghost" className="ml-auto text-alert" onClick={() => save("archived")}>Archive</Button></div>
    </DialogContent></Dialog>
  );
};

const Templates = ({ templates, reload }) => {
  const [starters, setStarters] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/inspections/starters").then((r) => setStarters(r.data));
  }, []);
  const imp = async (k) => {
    try {
      await api.post(`/inspections/starters/${k}`);
      toast.success("Added to your checklists");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inspections/templates/ai", { prompt, client_request_id: crypto.randomUUID() });
      const job = await pollJob(data.job_id);
      const { data: t } = await api.get(`/inspections/templates/${job.template_id}`);
      reload();
      setEdit(t);
      setPrompt("");
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    }
    setBusy(false);
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border bg-card p-5"><h2 className="flex items-center gap-2 font-semibold"><Sparkles className="h-4 w-4 text-health" />Build a checklist with AI</h2>
        <Textarea className="mt-3" rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Build a daily site safety inspection checklist for a maintenance company." data-testid="ai-checklist-prompt" />
        <Button className="mt-2" onClick={gen} disabled={busy || prompt.length < 10} data-testid="ai-checklist-btn">{busy ? "Building checklist…" : "Generate draft (1 credit)"}</Button></div>
      <div><h2 className="mb-3 font-semibold">Starter checklists</h2><div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{starters.map((s) => (
        <div key={s.key} className="flex items-center justify-between rounded-2xl border bg-card p-4"><div><p className="font-medium">{s.name}</p><p className="text-xs capitalize text-muted-foreground">{s.items} checks · {s.frequency.replace("_", "-")}</p></div>
          <Button size="sm" variant="outline" onClick={() => imp(s.key)} data-testid={`starter-${s.key}`}>Add</Button></div>))}</div></div>
      <div><h2 className="mb-3 font-semibold">Your checklists</h2><ul className="divide-y rounded-2xl border bg-card">{templates.map((t) => (
        <li key={t.id} className="flex items-center gap-3 p-4"><div className="flex-1"><p className="font-medium">{t.name}</p><p className="text-xs text-muted-foreground">{t.items.length} checks · {t.status}{t.ai_generated ? " · AI draft" : ""}</p></div>
          <Button size="sm" variant="ghost" onClick={() => setEdit(t)} data-testid={`edit-tpl-${t.id}`}><Pencil className="h-4 w-4" /></Button></li>))}
        {templates.length === 0 && <li className="p-4 text-sm text-muted-foreground">None yet — add a starter or build one with AI.</li>}</ul></div>
      {edit && <TemplateEditor t={edit} close={() => setEdit(null)} saved={reload} />}
    </div>
  );
};

export default function Inspections() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState(null);
  const [records, setRecords] = useState([]);
  const [start, setStart] = useState(null);
  const manager = MANAGERS.includes(user.role);
  const load = () => {
    api.get("/inspections/templates").then((r) => setTemplates(r.data));
    api.get("/inspections").then((r) => setRecords(r.data));
  };
  useEffect(() => {
    load();
  }, []);
  if (!templates) return <Loader />;
  const live = templates.filter((t) => t.status === "published");
  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="inspections-page">
      <div><p className="eyebrow">Inspections & checklists</p><h1 className="mt-2 text-3xl font-bold">Site inspections</h1>
        <p className="text-sm text-muted-foreground">Complete checks on your phone. Any failed check automatically raises a follow-up action.</p></div>
      <Tabs defaultValue="start"><TabsList><TabsTrigger value="start" data-testid="tab-start">Start</TabsTrigger><TabsTrigger value="records" data-testid="tab-records">Records ({records.length})</TabsTrigger>{manager && <TabsTrigger value="templates" data-testid="tab-templates">Checklists</TabsTrigger>}</TabsList>
        <TabsContent value="start" className="mt-6">{live.length === 0 ? <EmptyState icon={ClipboardCheck} title="No checklists published" text={manager ? "Open the Checklists tab to add a starter or build one with AI." : "Ask your manager to publish an inspection checklist."} /> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{live.map((t) => <button key={t.id} onClick={() => setStart(t)} data-testid={`start-${t.id}`} className="rounded-2xl border bg-card p-5 text-left transition-transform hover:-translate-y-0.5"><ClipboardCheck className="h-5 w-5 text-health" /><p className="mt-3 font-semibold">{t.name}</p><p className="text-xs capitalize text-muted-foreground">{t.items.length} checks · {t.frequency.replace("_", "-")}</p></button>)}</div>)}
        </TabsContent>
        <TabsContent value="records" className="mt-6"><div className="space-y-2">{records.map((r) => (
          <Link key={r.id} to={`/app/inspections/${r.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-4 hover:bg-accent/50" data-testid={`record-${r.id}`}>
            <span className="font-mono text-xs text-muted-foreground">{r.ref}</span><div className="min-w-0 flex-1"><p className="truncate font-medium">{r.template_name}</p><p className="text-xs text-muted-foreground">{r.location || "No location"} · {r.inspector_name} · {fmt(r.completed_at || r.started_at)}{r.follow_up_ref ? ` · follow-up of ${r.follow_up_ref}` : ""}</p></div>
            {r.score != null && <span className="font-mono text-sm">{r.score}%</span>}{r.failed_count > 0 && <span className="text-xs text-alert">{r.failed_count} failed</span>}
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ISTATUS[r.status][1]}`}>{ISTATUS[r.status][0]}</span></Link>))}
          {records.length === 0 && <p className="text-sm text-muted-foreground">No inspections yet.</p>}</div></TabsContent>
        {manager && <TabsContent value="templates" className="mt-6"><Templates templates={templates} reload={load} /></TabsContent>}
      </Tabs>
      {start && <StartDialog t={start} close={() => setStart(null)} />}
    </div>
  );
}
